import { Component, OnInit, Input, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import Chart from 'chart.js/auto';
import { DeviceService } from 'src/app/services/devices/device.service';
import formatPeriodsHoliday from './periodsHoliday';
import { DlCycleRegion, DlProgram, DlPulseRegion, DlScheduleService, DL_DAYS_OF_WEEK, DL_MINUTES_PER_DAY } from 'src/app/services/DL/dl-schedule.service';

interface DraggableScheduleMarker {
  key: string;
  programId: number;
  sourceIndex: number;
  dayIndex: number;
  minute: number;
  x: number;
  schedule: any;
}

interface SchedulerDragChangeEvent {
  programId: number;
  time: number;
  valueKind: 'absolute' | 'offset';
  isFinal?: boolean;
  dlKind?: 'pulse' | 'cycle' | 'switch';
  dlEdge?: 'start' | 'end';
}

interface ActiveDragState {
  chart: Chart;
  canvas: HTMLCanvasElement;
  marker: DraggableScheduleMarker;
}

interface DlCycleHoverTarget {
  cx: number;
  cy: number;
  radius: number;
  detail: CycleTooltipDetail;
  programId: number;
}

interface DlPulseHoverTarget {
  cx: number;
  cy: number;
  radius: number;
  detail: PulseTooltipDetail;
  programId: number;
}

interface CycleTooltipDetail {
  startTime: string;
  endTime: string;
  onLabel: string;
  offLabel: string;
}

interface PulseTooltipDetail {
  startTime: string;
  durationLabel: string;
}

interface ChartHoverTooltipState {
  visible: boolean;
  x: number;
  y: number;
  kind: 'cycle' | 'pulse' | null;
  cycle: CycleTooltipDetail | null;
  pulse: PulseTooltipDetail | null;
}

@Component({
    selector: 'app-scheduler-anc',
    imports: [CommonModule, TranslateModule],
    templateUrl: './scheduler-anc.component.html',
    styleUrl: './scheduler-anc.component.scss'
})
export class SchedulerAncComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  // Array de objetos de programación con estructura: {channel, daymask, festive, id, mode, time, type}
  @Input() schedulerData: any[] = [];
  // Permite personalizar el prefijo de IDs de los canvas
  @Input() chartIdPrefix: string = 'chart';
  /** Número de salidas del equipo (1, 2 o 4) */
  @Input() outputCount: number = 1;
  /** Salida seleccionada desde el componente padre */
  @Input() selectedOutput: number = 1;

  /** Datos de horarios de orto y ocaso para la semana */
  @Input() sunTimesData: any[] = [];

  /** Mostrar/ocultar la leyenda inferior (ON + orto/ocaso). */
  @Input() showLegend: boolean = true;


  @Input() serial: any;
  // Datos sobre los periodos festivos, con estructura: {enable: boolean, periods: Array<{enable, period, startDay, startMonth, endDay, endMonth, startHour, endHour}>}
  @Input() HolidayData: { enable?: boolean | number; periods?: any[] } | null = null;
  @Input() hoveredProgramId: number | null = null;
  /** Programas Datalog+ para marcar ciclos (inicio/fin) sin rellenar el timeline. */
  @Input() dlPrograms: DlProgram[] | null = null;
  @Input() dlActiveFestive = 0;

  @Output() schedulerDragChange = new EventEmitter<SchedulerDragChangeEvent>();

  // Referencias a los canvas de los gráficos
  charts: Chart[] = [];

  private canvasTooltipHandlers = new Map<string, { move: (e: MouseEvent) => void; leave: () => void }>();
  private canvasDragHandlers = new Map<string, {
    down: (e: MouseEvent) => void;
    move: (e: MouseEvent) => void;
    leave: () => void;
    up: (e: MouseEvent) => void;
  }>();
  private dragPreviewMinutes = new Map<string, number>();
  private dragLastEmitted = new Map<string, string>();
  private activeDrag: ActiveDragState | null = null;
  private dayEventsCache = new Map<string, Array<{ minute: number; action: 1 | 0; mode: number; priority: number }>>();
  private initialStateCache = new Map<string, boolean>();

  // Referencia al plugin de fondo día/noche
  private nightBackgroundPlugin: any = null;

  private viewInitialized = false;
  private cycleIconImage: HTMLImageElement | null = null;
  private pulseIconImage: HTMLImageElement | null = null;
  private hoveredDlMarkerProgramId: number | null = null;
  private dlWeekChartData: number[] | null = null;
  private dlWeekChartCacheKey = '';

  chartHoverTooltip: ChartHoverTooltipState = {
    visible: false,
    x: 0,
    y: 0,
    kind: null,
    cycle: null,
    pulse: null,
  };

  // Días de la semana (label en español solo para emparejar sunTimes API)
  daysOfWeek = DL_DAYS_OF_WEEK.map((day) => ({
    ...day,
    period: 0,
    periods: [] as number[],
  }));

  private readonly weekdayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  /** Colores originales del gráfico ON/OFF */
  private readonly chartOnBorderColor = '#4f81bd';
  private readonly chartOnBackgroundColor = 'rgba(79, 129, 189, 0.1)';

  holidayData:  any[] = [];
  originalData: any[] = [];


  constructor(
    private _device: DeviceService,
    private _dlSchedule: DlScheduleService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  getDayLabel(dayIndex: number): string {
    const key = this.weekdayKeys[dayIndex];
    return key ? this.t(key) : '';
  }

  getChartDatasetLabel(dayIndex: number): string {
    return `${this.t('state')} ${this.getDayLabel(dayIndex)}`;
  }

  ngOnInit(): void {
    this.preloadCycleIcon();
    this.preloadPulseIcon();
    this.applyHolidayInput();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['HolidayData']) {
      this.applyHolidayInput();
    }

    if (changes['sunTimesData']) {
      // Al cambiar el día base del gráfico, hay que recalcular qué periodo festivo cae en cada día.
      this.applyHolidayInput();
    }

    const shouldRebuild = Boolean(
      changes['selectedOutput'] ||
      changes['schedulerData'] ||
      changes['sunTimesData'] ||
      changes['HolidayData'] ||
      changes['dlPrograms'] ||
      changes['dlActiveFestive']
    );

    if (this.viewInitialized && shouldRebuild) {
      this.rebuildCharts();
      return;
    }

    if (this.viewInitialized && (changes['hoveredProgramId'] || changes['dlPrograms'])) {
      this.refreshChartVisualState();
    }
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.applyHolidayInput();
    setTimeout(() => this.createCharts(), 50);
  }

  private toHolidayTimeString(hourValue: any, minuteValue?: any, timeStringValue?: any): string | null {
    if (typeof timeStringValue === 'string' && /^\d{2}:\d{2}$/.test(timeStringValue)) {
      return timeStringValue;
    }

    if (typeof hourValue === 'string' && /^\d{2}:\d{2}$/.test(hourValue)) {
      return hourValue;
    }

    const hour = Number(hourValue);
    const minute = Number(minuteValue);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  private normalizeHolidayPeriod(period: any): any | null {
    if (!period) return null;

    const enabledValue = period.enable ?? period.enabled;
    if (enabledValue != null && Number(enabledValue) !== 1) return null;

    const periodNumber = Number(period.period);
    const key = Number.isFinite(periodNumber) && periodNumber >= 1 && periodNumber <= 4 ? `period${periodNumber}` : period.key;

    const startDay = Number(period.startDay);
    const startMonth = Number(period.startMonth);
    const endDay = Number(period.endDay);
    const endMonth = Number(period.endMonth);
    if (![startDay, startMonth, endDay, endMonth].every(Number.isFinite)) return null;

    const startHour = this.toHolidayTimeString(period.startHour, period.startMinute, period.startHourStr);
    const endHour = this.toHolidayTimeString(period.endHour, period.endMinute, period.endHourStr);
    if (!startHour || !endHour) return null;

    return {
      ...period,
      key,
      enabled: enabledValue == null ? 1 : Number(enabledValue),
      enable: enabledValue == null ? 1 : Number(enabledValue),
      startDay,
      startMonth,
      startHour,
      endDay,
      endMonth,
      endHour,
    };
  }

  private applyHolidayInput(): void {
    const inputPeriods = Array.isArray(this.HolidayData?.periods) ? this.HolidayData?.periods : [];
    const hasEnabledPeriods = inputPeriods.some((period: any) => Number(period?.enable ?? period?.enabled) === 1);
    const enabled = Boolean(this.HolidayData?.enable) || hasEnabledPeriods;

    if (!enabled || inputPeriods.length === 0) {
      this.holidayData = [];
      this.daysOfWeek = this.daysOfWeek.map(d => ({ ...d, period: 0, periods: [] }));
      this.clearComputationCaches();
      return;
    }

    // Normalizar periodos: asegurar `key` tipo 'period1'..'period4' y filtrar enable
    this.holidayData = inputPeriods
      .map((p: any) => this.normalizeHolidayPeriod(p))
      .filter((p: any): p is any => !!p);

    // Asignar periodo (0..4) a cada día
    this.daysOfWeek = this.daysOfWeek.map(d => ({
      ...d,
      periods: this.getHolidayPeriodsForDay(this.getDateForDayIndex(d.index)),
      period: this.getHolidayPeriodForDay(this.getDateForDayIndex(d.index))
    }));

    this.clearComputationCaches();
  }

  private clearComputationCaches(): void {
    this.dayEventsCache.clear();
    this.initialStateCache.clear();
    this.dlWeekChartData = null;
    this.dlWeekChartCacheKey = '';
  }

  private getDlWeekChartData(): number[] {
    if (!Array.isArray(this.dlPrograms) || this.dlPrograms.length === 0) return [];

    const output: 1 | 2 = this.selectedOutput === 2 ? 2 : 1;
    const festiveByDay = this.daysOfWeek.map((day) => Number(day.period) || 0);
    const cacheKey = `${output}:${festiveByDay.join(',')}:${this.dlPrograms.length}:${this.dlPrograms.map((program) =>
      `${program.id}|${program.kind}|${program.time}|${program.daymask}|${program.festive}|${program.channel}|${(program.auxdata || []).join(',')}`
    ).join(';')}`;

    if (this.dlWeekChartData && this.dlWeekChartCacheKey === cacheKey) {
      return this.dlWeekChartData;
    }

    this.dlWeekChartData = this._dlSchedule.buildWeekChartDataForDays(this.dlPrograms, festiveByDay, output);
    this.dlWeekChartCacheKey = cacheKey;
    return this.dlWeekChartData;
  }

  private rebuildCharts(): void {
    this.clearComputationCaches();
    this.charts.forEach(chart => chart.destroy());
    this.charts = [];
    setTimeout(() => this.createCharts(), 50);
  }

  private refreshChartVisualState(): void {
    this.charts.forEach((chart) => chart.update('none'));
  }

  ngOnDestroy(): void {
    // Limpiar los gráficos al destruir el componente
    this.charts.forEach(chart => chart.destroy());

    // Limpiar listeners de tooltips en canvas
    this.canvasTooltipHandlers.forEach((handlers, canvasId) => {
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
      if (canvas) {
        canvas.removeEventListener('mousemove', handlers.move);
        canvas.removeEventListener('mouseleave', handlers.leave);
      }
    });
    this.canvasTooltipHandlers.clear();

    this.canvasDragHandlers.forEach((handlers, canvasId) => {
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
      if (canvas) {
        canvas.removeEventListener('mousedown', handlers.down);
        canvas.removeEventListener('mousemove', handlers.move);
        canvas.removeEventListener('mouseleave', handlers.leave);
        canvas.style.cursor = '';
      }
      window.removeEventListener('mouseup', handlers.up);
    });
    this.canvasDragHandlers.clear();
    this.dragPreviewMinutes.clear();
    this.dragLastEmitted.clear();
    this.activeDrag = null;
    this.hoveredDlMarkerProgramId = null;
    this.hideChartHoverTooltip();

    // Unregister plugin if registered
    try {
      if (this.nightBackgroundPlugin) {
        Chart.unregister(this.nightBackgroundPlugin);
        this.nightBackgroundPlugin = null;
      }
    } catch (e) {
      console.warn('Error unregistering plugin', e);
    }
  }


  // Cambiar la salida seleccionada
  selectOutput(outputNumber: number): void {
    if (outputNumber >= 1 && outputNumber <= this.outputCount) {
      this.selectedOutput = outputNumber;
      // Recrear los gráficos con los nuevos datos
      this.charts.forEach(chart => chart.destroy());
      this.charts = [];
      setTimeout(() => {
        this.createCharts();
      }, 100);
    }
  }

  // Obtener los datos del scheduler para la salida seleccionada
  getSchedulerData(): any[] {

    var channel1 = [];
    var channel2 = [];
    this.schedulerData.forEach((element: any) => {
      if (element.channel == 0) return 0;
      if (element.channel == 1) channel1.push(element);
      if (element.channel == 2) channel2.push(element);
      if (element.channel == 3) {channel1.push(element); channel2.push(element);}
    })

    // Acceder al array de la salida seleccionada desde el objeto schedulerData
    // const outputKey = `output${this.selectedOutput}`;
    if (this.selectedOutput === 1) {
      return channel1;
    }else if (this.selectedOutput === 2) {
      return channel2;
    } else {  return [];  }
  }

  // Obtener los datos del scheduler para un día + periodo festivo activo
  getSchedulerDataForDay(dayIndex: number, activePeriodOverride?: number): any[] {
    const schedules = this.getSchedulerData();
    if (!schedules || schedules.length === 0) return [];

    const activePeriod = activePeriodOverride ?? (this.daysOfWeek?.[dayIndex]?.period ?? 0); // 0=no festivo, 1..4=periodo
    const dayBit = this.dayIndexToDayBit(dayIndex); // bit6=Lunes .. bit0=Domingo

    return schedules.filter((s: any) => {
      if (!s) return false;

      const daymask = Number(s.daymask) || 0;
      if ((daymask & dayBit) === 0) return false;

      const festive = Number(s.festive) || 0;

      // Interpretación “reemplaza”: en periodo N solo aplican festive=N (y en 0 solo festive=0)
      return festive === activePeriod;
    });
  }

  /** Convierte 0=Lunes..6=Domingo a bit6=Lunes..bit0=Domingo (Lunes=64, ..., Domingo=1) */
  private dayIndexToDayBit(dayIndex: number): number {
    if (!Number.isFinite(dayIndex)) return 0;
    if (dayIndex < 0 || dayIndex > 6) return 0;
    return 1 << (6 - dayIndex);
  }

  private toOurWeekdayIndex(date: Date): number {
    // JS: 0=Domingo..6=Sábado => Nuestro: 0=Lunes..6=Domingo
    const js = date.getDay();
    return js === 0 ? 6 : js - 1;
  }

  private getSunTimesBaseDate(): Date {
    const raw = (this.sunTimesData as any)?.[0]?.date;
    const d = raw instanceof Date ? new Date(raw) : (raw ? new Date(raw) : new Date());
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private getDateForDayIndex(dayIndex: number): Date {
    const base = this.getSunTimesBaseDate();
    const baseOurIndex = this.toOurWeekdayIndex(base);

    let offset = dayIndex - baseOurIndex;
    if (offset < 0) offset += 7;

    const d = new Date(base);
    d.setDate(base.getDate() + offset);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private parseHolidayTimeValue(value: string): { h: number; m: number } | null {
    if (typeof value !== 'string') return null;
    const [hhRaw, mmRaw] = value.split(':');
    const h = Number(hhRaw);
    const m = Number(mmRaw);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return { h, m };
  }

  private buildHolidayDate(year: number, month: number, day: number, hourText: string): Date | null {
    const time = this.parseHolidayTimeValue(hourText);
    if (!time) return null;

    const date = new Date(year, month - 1, day, time.h, time.m, 0, 0);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== (month - 1) ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private getHolidayRangesAroundDate(period: any, date: Date): Array<{ start: Date; end: Date; period: number }> {
    const periodIndex = Number(period?.period);
    const startDay = Number(period?.startDay);
    const startMonth = Number(period?.startMonth);
    const endDay = Number(period?.endDay);
    const endMonth = Number(period?.endMonth);
    const startHour = String(period?.startHour || '');
    const endHour = String(period?.endHour || '');

    if (!Number.isFinite(periodIndex) || periodIndex < 1 || periodIndex > 4) return [];
    if (![startDay, startMonth, endDay, endMonth].every(Number.isFinite)) return [];

    const ranges: Array<{ start: Date; end: Date; period: number }> = [];
    const yearsToTest = [date.getFullYear() - 1, date.getFullYear(), date.getFullYear() + 1];

    for (const baseYear of yearsToTest) {
      const start = this.buildHolidayDate(baseYear, startMonth, startDay, startHour);
      if (!start) continue;

      let endYear = baseYear;
      const crossesByMonthDay = (endMonth < startMonth) || (endMonth === startMonth && endDay < startDay);
      if (crossesByMonthDay) endYear = baseYear + 1;

      let end = this.buildHolidayDate(endYear, endMonth, endDay, endHour);
      if (!end) continue;

      if (end.getTime() <= start.getTime()) {
        end = this.buildHolidayDate(endYear + 1, endMonth, endDay, endHour) || end;
      }

      if (end.getTime() > start.getTime()) {
        ranges.push({ start, end, period: periodIndex });
      }
    }

    return ranges;
  }

  private getHolidayPeriodAtDate(date: Date): number {
    if (!Array.isArray(this.holidayData) || this.holidayData.length === 0) return 0;

    for (const period of this.holidayData) {
      const ranges = this.getHolidayRangesAroundDate(period, date);
      for (const range of ranges) {
        if (date >= range.start && date < range.end) {
          return range.period;
        }
      }
    }

    return 0;
  }

  private getHolidayPeriodAtMinute(dayIndex: number, minuteOfDay: number): number {
    const dayDate = this.getDateForDayIndex(dayIndex);
    const target = new Date(dayDate);
    target.setHours(0, 0, 0, 0);
    target.setMinutes(Math.max(0, Math.min(1439, Math.trunc(minuteOfDay))));
    return this.getHolidayPeriodAtDate(target);
  }

  private getHolidayPeriodForDay(dayDate: Date): number {
    const periods = this.getHolidayPeriodsForDay(dayDate);
    return periods.length ? periods[0] : 0;
  }

  private getHolidayPeriodsForDay(dayDate: Date): number[] {
    if (!Array.isArray(this.holidayData) || this.holidayData.length === 0) return [];

    const dayStart = new Date(dayDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayStart.getDate() + 1);

    const periods = new Set<number>();

    for (const period of this.holidayData) {
      const ranges = this.getHolidayRangesAroundDate(period, dayStart);
      for (const range of ranges) {
        if (range.start < dayEnd && range.end > dayStart) {
          const n = Number(range.period) || 0;
          if (n >= 1 && n <= 4) periods.add(n);
        }
      }
    }

    return Array.from(periods).sort((a, b) => a - b);
  }

  getDayBadgePeriods(day: any): number[] {
    if (!day) return [];
    if (Array.isArray(day.periods) && day.periods.length > 0) return day.periods;
    const period = Number(day.period);
    if (Number.isFinite(period) && period >= 1 && period <= 4) return [period];
    return [];
  }

  getPeriodBadgeClass(period: number): string {
    if (period === 1) return 'bg-primary';
    if (period === 2) return 'bg-success';
    if (period === 3) return 'bg-danger';
    if (period === 4) return 'bg-secondary';
    return 'bg-dark';
  }

  private getHolidayPeriodForDate(date: Date): number {
    return this.getHolidayPeriodAtDate(date);
  }

  // Crear los 7 gráficos de línea
  createCharts(): void {
    // Destruir gráficos existentes si los hay
    this.charts.forEach(chart => {
      if (chart) {
        chart.destroy();
      }
    });
    this.charts = [];

    this.daysOfWeek.forEach((day, index) => {
      const canvas = document.getElementById(`${this.chartIdPrefix}-${index}`) as HTMLCanvasElement;
      if (canvas) {
        const chart = this.createDayChart(canvas, day);
        this.charts.push(chart);
      }
    });
  }

  // Crear gráfico individual para un día
  createDayChart(canvas: HTMLCanvasElement, day: any): Chart {
    const ctx = canvas.getContext('2d')!;

    // Generar datos para las 24 horas del día
    const hourlyData = this.generateDayData(day.index);

    // Determinar si mostrar etiquetas de horas (solo en viernes y domingo)
    const showHourLabels = day.index === 4 || day.index === 6; // Viernes (4), Domingo (6)

    const sunMoonMarkersPlugin = {
      id: `sunMoonMarkers_${day.index}`,
      afterDatasetsDraw: (chartInstance: any) => {
        const chart = chartInstance;
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const xScale = chart.scales.x;

        const ortoStr = this.getOrtoTimeForDay(day.index);
        const ocasoStr = this.getOcasoTimeForDay(day.index);
        if (!ortoStr || !ocasoStr) return;

        const orto = this.parseTime(ortoStr);
        const ocaso = this.parseTime(ocasoStr);

        const ortoIndex = orto.hour * 60 + orto.minute;
        const ocasoIndex = ocaso.hour * 60 + ocaso.minute;

        const ortoX = xScale.getPixelForValue(ortoIndex);
        const ocasoX = xScale.getPixelForValue(ocasoIndex);

        ctx.save();
        ctx.strokeStyle = '#2e8eaa';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 6]);

        // Línea vertical + icono sol
        if (!isNaN(ortoX)) {
          ctx.beginPath();
          ctx.moveTo(ortoX, chartArea.top);
          ctx.lineTo(ortoX, chartArea.bottom);
          ctx.stroke();

          ctx.setLineDash([]);

          const sunY = chartArea.top + 15;
          ctx.beginPath();
          ctx.arc(ortoX, sunY, 10, 0, 2 * Math.PI);
          ctx.fillStyle = 'rgba(253, 184, 19, 0.25)';
          ctx.fill();
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.font = '10px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'rgba(0, 0, 0, 1)';
          ctx.fillText('☀️', ortoX, sunY);

          (chart as any).$sunMoonHoverTargets = {
            ...(chart as any).$sunMoonHoverTargets,
            sun: { x: ortoX, y: sunY, time: ortoStr }
          };

          ctx.strokeStyle = '#2e8eaa';
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 6]);
        }

        // Línea vertical + icono luna
        if (!isNaN(ocasoX)) {
          ctx.beginPath();
          ctx.moveTo(ocasoX, chartArea.top);
          ctx.lineTo(ocasoX, chartArea.bottom);
          ctx.stroke();

          ctx.setLineDash([]);

          const moonY = chartArea.top + 15;
          ctx.beginPath();
          ctx.arc(ocasoX, moonY, 10, 0, 2 * Math.PI);
          ctx.fillStyle = 'rgba(103, 58, 183, 0.25)';
          ctx.fill();
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.font = '10px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'rgba(0, 0, 0, 1)';
          ctx.fillText('🌙', ocasoX, moonY);

          (chart as any).$sunMoonHoverTargets = {
            ...(chart as any).$sunMoonHoverTargets,
            moon: { x: ocasoX, y: moonY, time: ocasoStr }
          };
        }

        ctx.restore();
      }
    };

    const draggableProgramMarkersPlugin = {
      id: `draggableProgramMarkers_${day.index}`,
      afterDatasetsDraw: (chartInstance: any) => {
        const chart = chartInstance as Chart;
        const xScale = chart.scales.x;
        const chartArea = chart.chartArea;
        const ctx = chart.ctx;
        const markers = this.getDraggableScheduleMarkers(day.index, xScale);

        (chart as any).$draggableMarkers = markers;

        if (!markers.length) {
          return;
        }

        ctx.save();
        for (const marker of markers) {
          const isActiveMarker = this.activeDrag?.chart === chart && this.activeDrag?.marker.key === marker.key;
          const hasHoveredProgram = this.hoveredProgramId !== null && this.hoveredProgramId !== undefined;
          const hoveredProgramId = hasHoveredProgram ? Number(this.hoveredProgramId) : NaN;
          const isHoveredProgram = hasHoveredProgram
            && Number.isFinite(hoveredProgramId)
            && hoveredProgramId === Number(marker.programId);
          const handleY = chartArea.bottom - 12;
          ctx.strokeStyle = isHoveredProgram ? '#0BDA51' : 'rgba(1, 30, 65, 0.35)';
          ctx.lineWidth = isActiveMarker ? 3 : (isHoveredProgram ? 3.5 : 2);
          ctx.setLineDash(isHoveredProgram ? [] : [4, 4]);
          ctx.beginPath();
          ctx.moveTo(marker.x, chartArea.top + 2);
          ctx.lineTo(marker.x, chartArea.bottom - 2);
          ctx.stroke();

          ctx.setLineDash([]);
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = isActiveMarker ? '#2e8eaa' : '#011e41';
          ctx.lineWidth = isActiveMarker ? 2.5 : 2;
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.beginPath();
          ctx.arc(marker.x, handleY, isActiveMarker ? 6 : (isHoveredProgram ? 6 : 5), 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
          ctx.shadowBlur = 0;

          if (isActiveMarker) {
            this.drawDragPreviewLabel(ctx, chartArea, marker.x, marker.minute);
          }
        }
        ctx.restore();
      }
    };

    const dlCycleMarkersPlugin = {
      id: `dlCycleMarkers_${day.index}`,
      afterDatasetsDraw: (chartInstance: any) => {
        if (!Array.isArray(this.dlPrograms) || this.dlPrograms.length === 0) return;

        const chart = chartInstance as Chart;
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const xScale = chart.scales.x;
        if (!chartArea || !xScale) return;

        const output: 1 | 2 = this.selectedOutput === 2 ? 2 : 1;
        const activeFestive = Number(day.period) || 0;
        const regions = this._dlSchedule.getCycleRegions(
          this.dlPrograms,
          activeFestive,
          output
        ).filter((region) => region.dayLabel === day.short);

        const hoverTargets: DlCycleHoverTarget[] = [];

        ctx.save();
        for (const region of regions) {
          const isHovered = (
            Number.isFinite(Number(this.hoveredProgramId)) && Number(this.hoveredProgramId) === region.programId
          ) || (
            Number.isFinite(Number(this.hoveredDlMarkerProgramId)) && Number(this.hoveredDlMarkerProgramId) === region.programId
          );

          const hitTarget = this.drawDlCycleRegionIcon(ctx, chartArea, xScale, region, isHovered);
          if (hitTarget) {
            hoverTargets.push({
              ...hitTarget,
              detail: this.buildCycleDetailTooltip(region),
              programId: region.programId,
            });
          }
        }
        ctx.restore();

        (chart as any).$dlCycleHoverTargets = hoverTargets;
      }
    };

    const dlPulseMarkersPlugin = {
      id: `dlPulseMarkers_${day.index}`,
      afterDatasetsDraw: (chartInstance: any) => {
        if (!Array.isArray(this.dlPrograms) || this.dlPrograms.length === 0) return;

        const chart = chartInstance as Chart;
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const xScale = chart.scales.x;
        if (!chartArea || !xScale) return;

        const output: 1 | 2 = this.selectedOutput === 2 ? 2 : 1;
        const activeFestive = Number(day.period) || 0;
        const regions = this.getPulseRegionsForDay(day.index, day.short);

        const hoverTargets: DlPulseHoverTarget[] = [];

        ctx.save();
        for (const region of regions) {
          const isHovered = (
            Number.isFinite(Number(this.hoveredProgramId)) && Number(this.hoveredProgramId) === region.programId
          ) || (
            Number.isFinite(Number(this.hoveredDlMarkerProgramId)) && Number(this.hoveredDlMarkerProgramId) === region.programId
          );

          const hitTarget = this.drawDlPulseRegionIcon(ctx, chartArea, xScale, region, isHovered);
          if (hitTarget) {
            hoverTargets.push({
              ...hitTarget,
              detail: this.buildPulseDetailTooltip(region),
              programId: region.programId,
            });
          }
        }
        ctx.restore();

        (chart as any).$dlPulseHoverTargets = hoverTargets;
      }
    };

    const chartPlugins = [sunMoonMarkersPlugin];
    if (Array.isArray(this.dlPrograms) && this.dlPrograms.length > 0) {
      chartPlugins.push(dlCycleMarkersPlugin);
    }
    chartPlugins.push(draggableProgramMarkersPlugin);
    if (Array.isArray(this.dlPrograms) && this.dlPrograms.length > 0) {
      chartPlugins.push(dlPulseMarkersPlugin);
    }

    const chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: Array.from({ length: 1441 }, (_, i) => {
          // Agregar un punto extra (1441) para mostrar la etiqueta 24:00
          if (i === 1440) return '24:00';
          const hour = Math.floor(i / 60);
          const minute = i % 60;
          return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        }),
        datasets: [{
          label: this.getChartDatasetLabel(day.index),
          data: hourlyData,
          borderColor: this.chartOnBorderColor,
          backgroundColor: this.chartOnBackgroundColor,
          borderWidth: 3,
          fill: true,
          stepped: true,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: this.chartOnBorderColor,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: showHourLabels ? 8 : 10, // Ratio más alto para gráficos más compactos
        plugins: {
          title: {
            display: false
          },
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          }
        },
        scales: {
          x: {
            display: true,
            ticks: {
              autoSkip: false,
              maxTicksLimit: 1442,
              callback: function(value, index) {
                // Solo mostrar etiquetas si está habilitado para este día
                if (!showHourLabels) {
                  return ''; // No mostrar etiquetas en L, M, X, J
                }

                // Solo mostrar las horas específicas: 0, 6, 12, 18, 24
                // Con 1441 puntos, cada hora corresponde a índices: hora * 60
                const targetIndices = [0, 360, 720, 1080, 1440]; // 0h, 6h, 12h, 18h, 24h
                if (targetIndices.includes(index)) {
                  let hour = Math.floor(index / 60);
                  // Caso especial para el último punto (índice 1440) que representa 24:00
                  if (index === 1440) hour = 24;
                  return `${hour.toString().padStart(2, '0')}:00`;
                }
                return '';
              },
              font: {
                size: 9
              },
              color: '#6c757d'
            },
            grid: {
              display: true,
              color: function(context) {
                // Solo mostrar líneas en las horas específicas: 0, 6, 12, 18, 24
                // Con 1441 puntos, cada hora corresponde a índices: hora * 60
                const targetIndices = [0, 360, 720, 1080, 1440]; // 0h, 6h, 12h, 18h, 24h
                if (targetIndices.includes(context.index)) {
                  return 'rgba(0, 0, 0, 0.2)';
                }

                return 'transparent'; // Ocultar todas las demás líneas
              },
              drawOnChartArea: true,
              drawTicks: true
            }
          },
          y: {
            title: {
              display: false
            },
            min: 0,
            max: 1,
            ticks: {
              display: false
            },
            grid: {
              display: true,
              color: function(context) {
                // Mostrar línea en el valor 0 (OFF) y valor 1 (ON)
                if (context.tick.value === 0 || context.tick.value === 1) {
                  return 'rgba(0, 0, 0, 0.1)';
                }
                return 'transparent';
              }
            }
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        },
        layout: {
          padding: {
            top: 0,
            bottom: 0,
            left: 10,
            right: 10
          }
        }
      },
      plugins: chartPlugins
    });

    // Plugin para pintar fondo día/noche basado en sunTimesData
    const nightBackgroundPlugin = {
      id: 'nightBackground',
      beforeDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const xScale = chart.scales.x;

        // Obtener horarios de orto y ocaso para este día específico
        const ortoStr = this.getOrtoTimeForDay(day.index);
        const ocasoStr = this.getOcasoTimeForDay(day.index);

        // Si no hay datos de sunTimes, no dibujar nada
        if (!ortoStr || !ocasoStr) return;

        const orto = this.parseTime(ortoStr);
        const ocaso = this.parseTime(ocasoStr);

        // Calcular índices para resolución de 1 minuto
        const ortoIndex = orto.hour * 60 + orto.minute;
        const ocasoIndex = ocaso.hour * 60 + ocaso.minute;

        // Obtener posiciones X en el gráfico
        const ortoX = xScale.getPixelForValue(ortoIndex);
        const ocasoX = xScale.getPixelForValue(ocasoIndex);

        ctx.save();

        // Color para noche (morado suave)
        const nightColor = 'rgba(103, 58, 183, 0.12)';
        // Color para día (blanco/transparente)
        const dayColor = 'rgba(255, 255, 255, 0)';

        // 1. Zona nocturna (madrugada): desde inicio hasta orto
        if (!isNaN(ortoX)) {
          ctx.fillStyle = nightColor;
          ctx.fillRect(chartArea.left, chartArea.top, ortoX - chartArea.left, chartArea.bottom - chartArea.top);
        }

        // 2. Zona diurna: desde orto hasta ocaso
        if (!isNaN(ortoX) && !isNaN(ocasoX)) {
          ctx.fillStyle = dayColor;
          ctx.fillRect(ortoX, chartArea.top, ocasoX - ortoX, chartArea.bottom - chartArea.top);
        }

        // 3. Zona nocturna (tarde/noche): desde ocaso hasta fin
        if (!isNaN(ocasoX)) {
          ctx.fillStyle = nightColor;
          ctx.fillRect(ocasoX, chartArea.top, chartArea.right - ocasoX, chartArea.bottom - chartArea.top);
        }

        ctx.restore();
      }
    };

    // Registrar plugin solo una vez
    if (!this.nightBackgroundPlugin) {
      this.nightBackgroundPlugin = nightBackgroundPlugin;
      Chart.register(this.nightBackgroundPlugin);
    }

    this.attachCanvasTooltips(canvas, chart);
    this.attachDragHandlers(canvas, chart, day.index);

    chart.update();

    return chart;
  }

  private getDraggableScheduleMarkers(dayIndex: number, xScale: any): DraggableScheduleMarker[] {
    const schedules = this.getSchedulerDataForDay(dayIndex);
    if (!Array.isArray(schedules) || !schedules.length || !xScale) return [];

    return schedules
      .map((schedule: any) => {
        const mode = Number(schedule?.mode);
        const dlKind = schedule?.dlKind;
        const isDlFixedTime = dlKind === 'pulse' || dlKind === 'cycle';
        if (!isDlFixedTime && ![1, 2, 4, 8, 16, 64, 128].includes(mode)) return null;

        if (schedule?.dlDraggable === false) return null;
        if (schedule?.dlKind === 'pulse' && schedule?.dlEdge !== 'start') return null;

        const sourceIndex = this.schedulerData.indexOf(schedule);
        if (sourceIndex === -1) return null;

        const markerKey = `${dayIndex}:${sourceIndex}:${schedule?.dlKind || 'switch'}:${schedule?.dlEdge || 'start'}`;
        const fallbackMinute = this.getMarkerDisplayMinute(schedule, dayIndex);
        if (fallbackMinute == null) return null;

        const minute = this.getPreviewMinute(markerKey, fallbackMinute);
        const x = xScale.getPixelForValue(minute);
        if (!Number.isFinite(x)) return null;

        const programId = Number(schedule?.id);
        return {
          key: markerKey,
          programId,
          sourceIndex,
          dayIndex,
          minute,
          x,
          schedule,
        } satisfies DraggableScheduleMarker;
      })
      .filter((marker: DraggableScheduleMarker | null): marker is DraggableScheduleMarker => !!marker);
  }

  private attachDragHandlers(canvas: HTMLCanvasElement, chart: Chart, dayIndex: number): void {
    const canvasId = canvas.id;
    const existing = this.canvasDragHandlers.get(canvasId);
    if (existing) {
      canvas.removeEventListener('mousedown', existing.down);
      canvas.removeEventListener('mousemove', existing.move);
      canvas.removeEventListener('mouseleave', existing.leave);
      window.removeEventListener('mouseup', existing.up);
    }

    const down = (e: MouseEvent) => {
      const marker = this.findDragMarker(chart, e, dayIndex);
      if (!marker) return;

      e.preventDefault();
      this.activeDrag = { chart, canvas, marker };
      canvas.style.cursor = 'ew-resize';
      this.updateDragPreview(chart, marker, e);
    };

    const move = (e: MouseEvent) => {
      if (this.activeDrag && this.activeDrag.chart === chart) {
        this.updateDragPreview(chart, this.activeDrag.marker, e);
        const previewMinute = this.dragPreviewMinutes.get(this.activeDrag.marker.key);
        this.emitDragChange(this.activeDrag.marker, previewMinute, false);
        return;
      }

      const marker = this.findDragMarker(chart, e, dayIndex);
      canvas.style.cursor = marker ? 'ew-resize' : '';
    };

    const leave = () => {
      if (!this.activeDrag || this.activeDrag.chart !== chart) {
        canvas.style.cursor = '';
      }
    };

    const up = (e: MouseEvent) => {
      if (!this.activeDrag || this.activeDrag.chart !== chart) return;

      this.updateDragPreview(chart, this.activeDrag.marker, e);
      const markerKey = this.activeDrag.marker.key;
      const finalMinute = this.dragPreviewMinutes.get(markerKey);
      this.emitDragChange(this.activeDrag.marker, finalMinute, true);

      this.dragPreviewMinutes.delete(markerKey);
      this.dragLastEmitted.delete(markerKey);
      this.activeDrag = null;
      canvas.style.cursor = '';
      chart.update('none');
    };

    canvas.addEventListener('mousedown', down);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseleave', leave);
    window.addEventListener('mouseup', up);

    this.canvasDragHandlers.set(canvasId, { down, move, leave, up });
  }

  private findDragMarker(chart: Chart, e: MouseEvent, dayIndex: number): DraggableScheduleMarker | null {
    const canvas = chart.canvas;
    const xScale = chart.scales.x;
    if (!canvas || !xScale) return null;

    const mouseX = this.getMouseXInCanvasPixels(canvas, e);
    const markers = ((chart as any).$draggableMarkers as DraggableScheduleMarker[] | undefined)
      ?? this.getDraggableScheduleMarkers(dayIndex, xScale);

    let nearest: DraggableScheduleMarker | null = null;
    let minDistance = Number.POSITIVE_INFINITY;

    for (const marker of markers) {
      const distance = Math.abs(marker.x - mouseX);
      if (distance <= 10 && distance < minDistance) {
        nearest = marker;
        minDistance = distance;
      }
    }

    return nearest;
  }

  private updateDragPreview(chart: Chart, marker: DraggableScheduleMarker, e: MouseEvent): void {
    const xScale = chart.scales.x;
    if (!xScale) return;

    const canvas = chart.canvas;
    const mouseX = this.getMouseXInCanvasPixels(canvas, e);
    const rawMinute = Number(xScale.getValueForPixel(mouseX));
    if (!Number.isFinite(rawMinute)) return;

    const minute = Math.max(0, Math.min(1439, Math.round(rawMinute)));
    this.dragPreviewMinutes.set(marker.key, minute);
    chart.update('none');
  }

  private getMouseXInCanvasPixels(canvas: HTMLCanvasElement, e: MouseEvent): number {
    const scaleX = canvas.clientWidth ? canvas.width / canvas.clientWidth : 1;
    return e.offsetX * scaleX;
  }

  private getPreviewMinute(markerKey: string, fallback: number): number {
    const preview = this.dragPreviewMinutes.get(markerKey);
    if (!Number.isFinite(preview)) return Math.max(0, Math.min(1439, Math.trunc(fallback)));
    return Math.max(0, Math.min(1439, Math.trunc(preview as number)));
  }

  private emitDragChange(marker: DraggableScheduleMarker, minute: number | undefined, isFinal: boolean = false): void {
    const dragChange = this.buildSchedulerDragChange(marker, minute);
    if (!dragChange) return;

    dragChange.isFinal = isFinal;

    const signature = `${dragChange.programId}:${dragChange.dlKind || ''}:${dragChange.dlEdge || ''}:${dragChange.valueKind}:${dragChange.time}:${dragChange.isFinal ? 1 : 0}`;
    if (this.dragLastEmitted.get(marker.key) === signature) return;

    this.dragLastEmitted.set(marker.key, signature);
    this.schedulerDragChange.emit(dragChange);
  }

  private getMarkerDisplayMinute(schedule: any, dayIndex: number): number | null {
    if (!schedule) return null;

    const mode = Number(schedule?.mode);
    if (mode === 1) {
      return this.getAdjustedSunMinuteForDay(dayIndex, 'sunset');
    }

    if (mode === 2) {
      return this.getAdjustedSunMinuteForDay(dayIndex, 'sunrise');
    }

    const time = Number(schedule?.time);
    if (!Number.isFinite(time)) return null;
    return Math.max(0, Math.min(1439, Math.trunc(time)));
  }

  private buildSchedulerDragChange(
    marker: DraggableScheduleMarker,
    finalMinute: number | undefined,
  ): SchedulerDragChangeEvent | null {
    if (!marker || !Number.isFinite(finalMinute)) return null;

    const minute = Math.max(0, Math.min(1439, Math.trunc(finalMinute as number)));
    const programId = Number(marker.programId);
    if (!Number.isFinite(programId)) return null;

    const mode = Number(marker.schedule?.mode);
    if (mode === 1) {
      const baseMinute = this.getBaseSunMinuteForDay(marker.dayIndex, 'sunset');
      if (baseMinute == null) return null;
      return { programId, time: minute - baseMinute, valueKind: 'offset' };
    }

    if (mode === 2) {
      const baseMinute = this.getBaseSunMinuteForDay(marker.dayIndex, 'sunrise');
      if (baseMinute == null) return null;
      return { programId, time: minute - baseMinute, valueKind: 'offset' };
    }

    return {
      programId,
      time: minute,
      valueKind: 'absolute',
      dlKind: marker.schedule?.dlKind,
      dlEdge: marker.schedule?.dlEdge,
    };
  }

  private getAdjustedSunMinuteForDay(dayIndex: number, type: 'sunrise' | 'sunset'): number | null {
    const adjusted = this.getAdjustedSunTimeForDay(dayIndex, type);
    const minute = adjusted.hour * 60 + adjusted.minute;
    return Number.isFinite(minute) ? Math.max(0, Math.min(1439, minute)) : null;
  }

  private getBaseSunMinuteForDay(dayIndex: number, type: 'sunrise' | 'sunset'): number | null {
    const baseStr = type === 'sunrise' ? this.getOrtoTimeForDay(dayIndex) : this.getOcasoTimeForDay(dayIndex);
    if (!baseStr) return null;

    const base = this.parseTime(baseStr);
    const minute = base.hour * 60 + base.minute;
    return Number.isFinite(minute) ? Math.max(0, Math.min(1439, minute)) : null;
  }

  private drawDragPreviewLabel(
    ctx: CanvasRenderingContext2D,
    chartArea: { left: number; right: number; top: number; bottom: number },
    x: number,
    minute: number
  ): void {
    const label = this.formatMinutesAsTime(minute);
    ctx.save();
    ctx.font = '600 11px Arial';
    const textWidth = ctx.measureText(label).width;
    const paddingX = 8;
    const boxWidth = textWidth + paddingX * 2;
    const boxHeight = 22;
    const minX = chartArea.left + 2;
    const maxX = chartArea.right - boxWidth - 2;
    const boxX = Math.max(minX, Math.min(x - (boxWidth / 2), maxX));
    const boxY = chartArea.top + 20;

    ctx.fillStyle = 'rgba(1, 30, 65, 0.92)';
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 6);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, boxX + (boxWidth / 2), boxY + (boxHeight / 2) + 0.5);
    ctx.restore();
  }

  private formatMinutesAsTime(totalMinutes: number): string {
    const minute = Math.max(0, Math.min(1439, Math.trunc(totalMinutes)));
    const hours = Math.floor(minute / 60);
    const minutes = minute % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  private attachCanvasTooltips(canvas: HTMLCanvasElement, chart: Chart): void {
    const canvasId = canvas.id;

    const existing = this.canvasTooltipHandlers.get(canvasId);
    if (existing) {
      canvas.removeEventListener('mousemove', existing.move);
      canvas.removeEventListener('mouseleave', existing.leave);
    }

    const move = (e: MouseEvent) => {
      if (this.activeDrag) return;

      const scaleX = canvas.clientWidth ? canvas.width / canvas.clientWidth : 1;
      const scaleY = canvas.clientHeight ? canvas.height / canvas.clientHeight : 1;
      const mouseX = e.offsetX * scaleX;
      const mouseY = e.offsetY * scaleY;

      const dist2 = (x1: number, y1: number) => {
        const dx = mouseX - x1;
        const dy = mouseY - y1;
        return dx * dx + dy * dy;
      };

      const cycleTargets = (chart as any).$dlCycleHoverTargets as DlCycleHoverTarget[] | undefined;
      let cycleHit: DlCycleHoverTarget | null = null;

      if (Array.isArray(cycleTargets)) {
        for (const target of cycleTargets) {
          const radius = (target.radius || 14) * Math.max(scaleX, scaleY);
          if (dist2(target.cx, target.cy) <= radius * radius) {
            cycleHit = target;
            break;
          }
        }
      }

      if (cycleHit) {
        const nextProgramId = Number(cycleHit.programId);
        if (this.hoveredDlMarkerProgramId !== nextProgramId) {
          this.hoveredDlMarkerProgramId = Number.isFinite(nextProgramId) ? nextProgramId : null;
          chart.update('none');
        }

        this.showChartHoverTooltip(canvas, e, { kind: 'cycle', detail: cycleHit.detail });
        if (canvas.title) canvas.title = '';
        return;
      }

      const pulseTargets = (chart as any).$dlPulseHoverTargets as DlPulseHoverTarget[] | undefined;
      let pulseHit: DlPulseHoverTarget | null = null;

      if (Array.isArray(pulseTargets)) {
        for (const target of pulseTargets) {
          const radius = (target.radius || 14) * Math.max(scaleX, scaleY);
          if (dist2(target.cx, target.cy) <= radius * radius) {
            pulseHit = target;
            break;
          }
        }
      }

      if (pulseHit) {
        const nextProgramId = Number(pulseHit.programId);
        if (this.hoveredDlMarkerProgramId !== nextProgramId) {
          this.hoveredDlMarkerProgramId = Number.isFinite(nextProgramId) ? nextProgramId : null;
          chart.update('none');
        }

        this.showChartHoverTooltip(canvas, e, { kind: 'pulse', detail: pulseHit.detail });
        if (canvas.title) canvas.title = '';
        return;
      }

      if (this.hoveredDlMarkerProgramId !== null) {
        this.hoveredDlMarkerProgramId = null;
        chart.update('none');
      }
      this.hideChartHoverTooltip();

      const targets = (chart as any).$sunMoonHoverTargets as
        | { sun?: { x: number; y: number; time: string }; moon?: { x: number; y: number; time: string } }
        | undefined;

      if (!targets || (!targets.sun && !targets.moon)) {
        if (canvas.title) canvas.title = '';
        return;
      }

      const hitRadius = 14 * Math.max(scaleX, scaleY);
      const hitRadius2 = hitRadius * hitRadius;

      let title = '';
      if (targets.sun && !isNaN(targets.sun.x) && !isNaN(targets.sun.y) && dist2(targets.sun.x, targets.sun.y) <= hitRadius2) {
        title = `${this.t('sunrise')}: ${targets.sun.time}`;
      } else if (targets.moon && !isNaN(targets.moon.x) && !isNaN(targets.moon.y) && dist2(targets.moon.x, targets.moon.y) <= hitRadius2) {
        title = `${this.t('sunset')}: ${targets.moon.time}`;
      }

      if (canvas.title !== title) {
        canvas.title = title;
      }
    };

    const leave = () => {
      if (canvas.title) canvas.title = '';
      if (this.hoveredDlMarkerProgramId !== null) {
        this.hoveredDlMarkerProgramId = null;
        chart.update('none');
      }
      this.hideChartHoverTooltip();
    };

    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseleave', leave);
    this.canvasTooltipHandlers.set(canvasId, { move, leave });
  }

  private showChartHoverTooltip(
    canvas: HTMLCanvasElement,
    e: MouseEvent,
    payload: { kind: 'cycle'; detail: CycleTooltipDetail } | { kind: 'pulse'; detail: PulseTooltipDetail }
  ): void {
    const container = canvas.closest('.charts-container') as HTMLElement | null;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const tooltipWidth = 220;
    const tooltipHeight = payload.kind === 'pulse' ? 64 : 72;
    let left = e.clientX - containerRect.left + 12;
    let top = e.clientY - containerRect.top - tooltipHeight - 10;

    left = Math.max(8, Math.min(containerRect.width - tooltipWidth - 8, left));
    top = Math.max(8, Math.min(containerRect.height - tooltipHeight - 8, top));

    const next: ChartHoverTooltipState = {
      visible: true,
      x: left,
      y: top,
      kind: payload.kind,
      cycle: payload.kind === 'cycle' ? payload.detail : null,
      pulse: payload.kind === 'pulse' ? payload.detail : null,
    };

    const current = this.chartHoverTooltip;
    if (
      current.visible === next.visible
      && current.x === next.x
      && current.y === next.y
      && current.kind === next.kind
      && current.cycle?.startTime === next.cycle?.startTime
      && current.cycle?.endTime === next.cycle?.endTime
      && current.cycle?.onLabel === next.cycle?.onLabel
      && current.cycle?.offLabel === next.cycle?.offLabel
      && current.pulse?.startTime === next.pulse?.startTime
      && current.pulse?.durationLabel === next.pulse?.durationLabel
    ) {
      return;
    }

    this.chartHoverTooltip = next;
    this.cdr.markForCheck();
  }

  private hideChartHoverTooltip(): void {
    if (!this.chartHoverTooltip.visible) return;
    this.chartHoverTooltip = { visible: false, x: 0, y: 0, kind: null, cycle: null, pulse: null };
    this.cdr.markForCheck();
  }

  private buildCycleDetailTooltip(region: DlCycleRegion): CycleTooltipDetail {
    return {
      startTime: region.timeLabel,
      endTime: region.endTimeLabel,
      onLabel: this._dlSchedule.formatDurationLabel(region.onSeconds),
      offLabel: this._dlSchedule.formatDurationLabel(region.offSeconds),
    };
  }

  private getPulseRegionsForDay(dayIndex: number, dayShort: string): DlPulseRegion[] {
    const output: 1 | 2 = this.selectedOutput === 2 ? 2 : 1;
    const activeFestive = Number(this.daysOfWeek[dayIndex]?.period) || 0;
    const fromPrograms = this._dlSchedule.getPulseRegions(this.dlPrograms || [], activeFestive, output)
      .filter((region) => region.dayLabel === dayShort);

    if (fromPrograms.length > 0) {
      return fromPrograms;
    }

    if (!Array.isArray(this.dlPrograms) || !this.schedulerData?.length) {
      return [];
    }

    const schedules = this.getSchedulerDataForDay(dayIndex);
    const dayOffset = dayIndex * DL_MINUTES_PER_DAY;
    const fallback: DlPulseRegion[] = [];

    schedules.forEach((schedule: any) => {
      if (schedule?.dlKind !== 'pulse' || schedule?.dlEdge !== 'start') return;

      const programId = Number(schedule.id);
      const program = (this.dlPrograms || []).find((item) => Number(item.id) === programId);
      if (!program || !this._dlSchedule.isPulseProgram(program)) return;

      const startMinute = this.getEventMinuteForSchedule(schedule, dayIndex);
      if (startMinute == null) return;

      const durationSeconds = Math.max(1, this._dlSchedule.getPulseDurationSeconds(program));
      const durationMinutes = Math.max(1, Math.ceil(durationSeconds / 60));
      const endMinute = Math.min(DL_MINUTES_PER_DAY, startMinute + durationMinutes);
      const weekMinuteStart = dayOffset + startMinute;
      const weekMinuteEnd = dayOffset + endMinute;

      fallback.push({
        programId,
        weekMinuteStart,
        weekMinuteEnd,
        centerWeekMinute: weekMinuteStart + ((endMinute - startMinute) / 2),
        startMinuteOfDay: startMinute,
        endMinuteOfDay: endMinute,
        startSecondOfDay: startMinute * 60,
        durationSeconds,
        timeLabel: this._dlSchedule.formatMinuteLabel(startMinute),
        timeSecondLabel: this._dlSchedule.formatSecondLabel(startMinute * 60),
        dayLabel: dayShort,
      });
    });

    return fallback;
  }

  private buildPulseDetailTooltip(region: DlPulseRegion): PulseTooltipDetail {
    return {
      startTime: region.timeLabel,
      durationLabel: this._dlSchedule.formatPulseDurationLabel(region.durationSeconds),
    };
  }

  // Generar datos para un día: misma base ON/OFF que scheduleData + ciclo encima
  generateDayData(dayIndex: number): number[] {
    const hourlyData: number[] = [];
    const output: 1 | 2 = this.selectedOutput === 2 ? 2 : 1;
    const activeFestive = Number(this.daysOfWeek[dayIndex]?.period) || 0;
    const hasCycles = Array.isArray(this.dlPrograms)
      && this.dlPrograms.some((program) => program.kind === 'cycle');

    for (let minute = 0; minute < 1440; minute++) {
      const hour = Math.floor(minute / 60);
      const min = minute % 60;

      let value: 0 | 1 = this.isTimeActive(dayIndex, hour, min) ? 1 : 0;

      if (hasCycles && this.dlPrograms) {
        const cycleValue = this._dlSchedule.getCycleChartValueAtMinute(
          this.dlPrograms,
          dayIndex,
          minute,
          activeFestive,
          output
        );
        if (cycleValue !== null) {
          value = cycleValue;
        }
      }

      hourlyData.push(value);
    }

    hourlyData.push(hourlyData[1439]);
    return hourlyData;
  }

  private getEventMinuteForSchedule(
    schedule: any,
    dayIndex: number,
    activePeriodOverride?: number
  ): number | null {
    if (!schedule) return null;

    const mode = Number(schedule.mode);
    const t = Number(schedule.time);

    // 16/4/8/64/128 = hora fija (time = minutos 0..1439); pulsos/ciclos DL usan 64/128
    if (mode === 16 || mode === 4 || mode === 8 || mode === 64 || mode === 128) {
      if (!Number.isFinite(t)) return null;
      return Math.max(0, Math.min(1439, Math.trunc(t)));
    }

    // 1 = ocaso (con offset), 2 = orto (con offset)
    if (mode === 1) {
      const ocaso = this.getAdjustedSunTimeForDay(dayIndex, 'sunset', activePeriodOverride);
      return Math.max(0, Math.min(1439, (ocaso.hour * 60 + ocaso.minute) | 0));
    }

    if (mode === 2) {
      const orto = this.getAdjustedSunTimeForDay(dayIndex, 'sunrise', activePeriodOverride);
      return Math.max(0, Math.min(1439, (orto.hour * 60 + orto.minute) | 0));
    }

    return null;
  }

  private buildDayEvents(dayIndex: number, activePeriodOverride?: number): Array<{ minute: number; action: 1 | 0; mode: number; priority: number }> {
    const cacheKey = `${dayIndex}:${activePeriodOverride ?? 'auto'}`;
    const cached = this.dayEventsCache.get(cacheKey);
    if (cached) return cached;

    const schedules = this.getSchedulerDataForDay(dayIndex, activePeriodOverride);
    if (!schedules || schedules.length === 0) {
      this.dayEventsCache.set(cacheKey, []);
      return [];
    }

    type Event = { minute: number; action: 1 | 0; mode: number; priority: number };

    const rawEvents: Event[] = schedules
      .map((schedule: any) => {
        const mode = Number(schedule?.mode) || 0;
        const minute = this.getEventMinuteForSchedule(schedule, dayIndex, activePeriodOverride);
        if (minute == null) return null;

        const action: 1 | 0 = Number(schedule?.type) === 1 ? 1 : 0;
        // Para empates (mismo minuto), la prioridad alta se aplica al final.
        // - mode 4 (INIC REDUC) tiene prioridad sobre mode 1
        // - mode 2 debe poder imponerse sobre mode 8 si coinciden
        const priority = mode === 4 ? 3 : mode === 2 ? 2 : 1;

        return { minute, action, mode, priority } as Event;
      })
      .filter((e: any): e is Event => !!e);

    // Prioridades de cancelación (igual que en A2):
    // - ON mode=1 se ignora si existe OFF mode=4 anterior
    // - ON mode=8 se ignora si existe OFF mode=2 anterior
    const mode4OffMinutes = rawEvents
      .filter((e) => e.mode === 4 && e.action === 0)
      .map((e) => e.minute)
      .sort((a, b) => a - b);

    const mode2OffMinutes = rawEvents
      .filter((e) => e.mode === 2 && e.action === 0)
      .map((e) => e.minute)
      .sort((a, b) => a - b);

    const sunriseMinute = this.getAdjustedSunTimeForDay(dayIndex, 'sunrise', activePeriodOverride);
    const sunriseBaseMinute = (sunriseMinute.hour * 60 + sunriseMinute.minute) | 0;

    const filtered = rawEvents.filter((e) => {
      if (e.mode === 1 && e.action === 1 && mode4OffMinutes.length > 0) {
        const ocasoMinute = e.minute;
        const hasBlockingMode4Off = mode4OffMinutes.some(
          (m4) => m4 >= sunriseBaseMinute && m4 <= ocasoMinute
        );
        return !hasBlockingMode4Off;
      }

      if (e.mode === 8 && e.action === 1 && mode2OffMinutes.length > 0) {
        const hasBlockingMode2Off = mode2OffMinutes.some(
          (m2) => m2 >= sunriseBaseMinute && m2 <= e.minute
        );
        return !hasBlockingMode2Off;
      }

      return true;
    });

    filtered.sort((a, b) => (a.minute - b.minute) || (a.priority - b.priority));
    this.dayEventsCache.set(cacheKey, filtered);
    return filtered;
  }

  isTimeActive(dayIndex: number, hour: number, minute: number): boolean {
    const currentMinutes = hour * 60 + minute;
    const activePeriod = this.getHolidayPeriodAtMinute(dayIndex, currentMinutes);

    // Obtener el estado inicial del día para el periodo activo en ese minuto
    let currentState = this.getInitialDayState(dayIndex, activePeriod);

    const dayEvents = this.buildDayEvents(dayIndex, activePeriod);
    if (dayEvents.length === 0) return currentState;

    for (const event of dayEvents) {
      if (currentMinutes >= event.minute) {
        currentState = event.action === 1;
      } else {
        break;
      }
    }

    return currentState;
  }

  // Obtener el estado inicial de un día basado en lo que pasó el día anterior
  getInitialDayState(dayIndex: number, activePeriodOverride?: number): boolean {
    const cacheKey = `${dayIndex}:${activePeriodOverride ?? 'auto'}`;
    const cached = this.initialStateCache.get(cacheKey);
    if (cached != null) return cached;

    const dayDate = this.getDateForDayIndex(dayIndex);
    const prevDate = new Date(dayDate);
    prevDate.setDate(dayDate.getDate() - 1);
    prevDate.setHours(0, 0, 0, 0);

    const previousDayIndex = this.toOurWeekdayIndex(prevDate);
    const previousDayPeriod = activePeriodOverride ?? this.getHolidayPeriodForDate(prevDate);

    const previousDayEvents = this.buildDayEvents(previousDayIndex, previousDayPeriod);
    if (previousDayEvents.length === 0) {
      this.initialStateCache.set(cacheKey, false);
      return false;
    }

    let previousDayFinalState = false;
    for (const event of previousDayEvents) {
      previousDayFinalState = event.action === 1;
    }

    this.initialStateCache.set(cacheKey, previousDayFinalState);
    return previousDayFinalState;
  }

  // Parsear tiempo incluyendo minutos
  parseTime(time: string | number): {hour: number, minute: number} {
    if (typeof time === 'number') {
      if (time >= 0) {
        return {
          hour: Math.floor(time / 60),
          minute: time % 60
        };
      } else {
        // Offset negativo, devolver 0:0
        return {hour: 0, minute: 0};
      }
    }
    if (!time) return {hour: 0, minute: 0};
    const [hours, minutes] = time.split(':').map(Number);
    return {
      hour: hours || 0,
      minute: minutes || 0
    };
  }

  // Verificar si un día está activo en una programación específica
  isDayActiveInSchedule(schedule: any, dayIndex: number): boolean {
    if (!schedule || !schedule.daymask) return false;
    const daymask = Number(schedule.daymask) || 0;
    const dayBit = this.dayIndexToDayBit(dayIndex);
    return (daymask & dayBit) !== 0;
  }

  // Obtener tiempo de orto de la programación
  getOrtoTime(): string {
    const schedules = this.getSchedulerData();
    console.log('----------- schedules for orto', schedules);

    if (schedules.length === 0) return '07:20';

    console.log('----------- schedules for orto', schedules);
    const sunriseSchedule = schedules.find((s: any) => s.type === 'sunrise');
    console.log('---------------- sunriseSchedule',sunriseSchedule);
    return sunriseSchedule ? sunriseSchedule.time : '07:20';
  }

  // Obtener tiempo de ocaso de la programación
  getOcasoTime(): string {
    const schedules = this.getSchedulerData();
    if (schedules.length === 0) return '18:40';

    const sunsetSchedule = schedules.find((s: any) => s.type === 'sunset');
    return sunsetSchedule ? sunsetSchedule.time : '18:40';
  }

  // Obtener tiempo de orto para un día específico usando sunTimesData
  getOrtoTimeForDay(dayIndex: number): string {
    // Si tenemos sunTimesData, usar esos valores reales
    if (this.sunTimesData && this.sunTimesData.length > 0) {
      // sunTimesData es un array de 7 días empezando desde HOY (fecha actual)
      // pero dayIndex representa el día de la semana: 0=Lunes, 1=Martes, etc.
      // Necesitamos mapear correctamente
      const dataIndex = this.mapDayIndexToDataIndex(dayIndex);
      if (dataIndex >= 0 && dataIndex < this.sunTimesData.length) {
        const dayData = this.sunTimesData[dataIndex];
        if (dayData && dayData.sunriseStr) {
          return dayData.sunriseStr;
        }
      }
    }
    // Fallback: usar el método antiguo basado en scheduler
    return '';
  }

  // Obtener tiempo de ocaso para un día específico usando sunTimesData
  getOcasoTimeForDay(dayIndex: number): string {
    // Si tenemos sunTimesData, usar esos valores reales
    if (this.sunTimesData && this.sunTimesData.length > 0) {
      // sunTimesData es un array de 7 días empezando desde HOY (fecha actual)
      // pero dayIndex representa el día de la semana: 0=Lunes, 1=Martes, etc.
      // Necesitamos mapear correctamente
      const dataIndex = this.mapDayIndexToDataIndex(dayIndex);
      if (dataIndex >= 0 && dataIndex < this.sunTimesData.length) {
        const dayData = this.sunTimesData[dataIndex];
        if (dayData && dayData.sunsetStr) {
          return dayData.sunsetStr;
        }
      }
    }
    // Fallback: usar el método antiguo basado en scheduler
    return this.getOcasoTime();
  }

  /**
   * Devuelve la hora ajustada de orto/ocaso para un día concreto.
   * Si existe una programación tipo 'sunrise'/'sunset' activa ese día con un offset,
   * aplica el offset al tiempo astronómico base.
   */
  getAdjustedSunTimeForDay(
    dayIndex: number,
    type: 'sunrise' | 'sunset',
    activePeriodOverride?: number
  ): {hour:number, minute:number} {
    // Obtener tiempo base desde sunTimesData o scheduler fallback
    const baseStr = type === 'sunrise' ? this.getOrtoTimeForDay(dayIndex) : this.getOcasoTimeForDay(dayIndex);
    const base = this.parseTime(baseStr);

    // Buscar una programación específica de este tipo activa para el día
    const schedules = this.getSchedulerDataForDay(dayIndex, activePeriodOverride) || [];
    const targetMode = type === 'sunrise' ? 2 : 1;
    const sched = schedules.find((s: any) => Number(s?.mode) === targetMode);

    let minutes = base.hour * 60 + base.minute;
    if (sched && typeof sched.time === 'number' && !isNaN(sched.time)) {
      minutes += sched.time; // offset en minutos (positivo o negativo)
    }

    // Normalizar
    if (minutes < 0) minutes = 0;
    if (minutes > 24 * 60) minutes = 24 * 60;

    return { hour: Math.floor(minutes / 60), minute: minutes % 60 };
  }

  /**
   * Mapea el índice del día de la semana (0=Lunes, 1=Martes, ..., 6=Domingo)
   * al índice correspondiente en sunTimesData (que empieza desde hoy)
   */
  private mapDayIndexToDataIndex(dayIndex: number): number {
    if (!this.sunTimesData || this.sunTimesData.length === 0) return -1;

    // Obtener el día de la semana de hoy (0=Domingo, 1=Lunes, ..., 6=Sábado en JS)
    const today = new Date();
    const todayJsWeekday = today.getDay(); // 0=Domingo, 1=Lunes, ..., 6=Sábado

    // Convertir de JS weekday (0=Domingo) a nuestro índice (0=Lunes, 6=Domingo)
    const todayOurIndex = todayJsWeekday === 0 ? 6 : todayJsWeekday - 1;

    // Calcular el offset desde hoy hasta el día solicitado
    // Si dayIndex < todayOurIndex, el día ya pasó esta semana, será la próxima
    let offset = dayIndex - todayOurIndex;
    if (offset < 0) {
      offset += 7; // Ajustar para la próxima semana
    }

    return offset;
  }




  getDayOfWeek(){
    const date = new Date();
    const day = date.getDay(); // 0 (Domingo) a 6 (Sábado)
    if (day === 0){
      return 6;
    }
    return day - 1;
  }


    /**
   * Public API: pause rendering and clean up icons without destroying the component.
   * Useful when another modal/chart must be shown on top to avoid visual overlap.
   */
  public pauseRendering(): void {
    try {
      // Destroy created charts
      this.charts.forEach(c => c.destroy());
      this.charts = [];

      // Unregister plugin if present
      if (this.nightBackgroundPlugin) {
        try { Chart.unregister(this.nightBackgroundPlugin); } catch (e) { /* ignore */ }
        this.nightBackgroundPlugin = null;
      }

      // Remove any icon nodes
      const orphans = document.querySelectorAll('.orto-ocaso-icon');
      orphans.forEach((n: any) => n.remove());
    } catch (e) {
      console.warn('pauseRendering error', e);
    }
  }

  /**
   * Public API: resume rendering by recreating charts. Will re-register plugin as needed.
   */
  public resumeRendering(): void {
    try {
      // Recreate charts after a short delay to ensure DOM layout stabilized
      setTimeout(() => this.createCharts(), 50);
    } catch (e) {
      console.warn('resumeRendering error', e);
    }
  }

  periodNumberFromKey(key: string | null): number {
    if (!key) return 0;
    const match = key.match(/^period(\d)$/i);
    if (!match) return 0;
    const n = Number(match[1]);
    return Number.isFinite(n) ? n : 0;
  }

    isDaySelected(daymask: unknown, dayValue: number): boolean {
    const mask = Number(daymask) || 0;
    return (mask & dayValue) !== 0;
  }

  toggleDay(program: any, dayValue: number): void {
    if (!program) return;
    const mask = Number(program.daymask) || 0;
    program.daymask = (mask ^ dayValue) & 0x7f;
  }

  private preloadCycleIcon(): void {
    const image = new Image();
    image.src = 'assets/img/orbis/cycle-icon.svg';
    image.onload = () => {
      this.cycleIconImage = image;
      if (this.viewInitialized) {
        this.refreshChartVisualState();
      }
    };
    image.onerror = () => {
      this.cycleIconImage = null;
    };
  }

  private preloadPulseIcon(): void {
    const image = new Image();
    image.src = 'assets/img/orbis/pulse-icon.svg';
    image.onload = () => {
      this.pulseIconImage = image;
      if (this.viewInitialized) {
        this.refreshChartVisualState();
      }
    };
    image.onerror = () => {
      this.pulseIconImage = null;
    };
  }

  private drawDlCycleRegionIcon(
    ctx: CanvasRenderingContext2D,
    chartArea: { top: number; bottom: number; left: number; right: number },
    xScale: { getPixelForValue: (value: number) => number },
    region: DlCycleRegion,
    isHovered: boolean
  ): { cx: number; cy: number; radius: number } | null {
    const startMinute = Math.max(0, Math.min(1440, region.startMinuteOfDay));
    const endMinute = Math.max(0, Math.min(1440, region.endMinuteOfDay));
    if (endMinute <= startMinute) return null;

    const startX = xScale.getPixelForValue(startMinute);
    const endX = xScale.getPixelForValue(endMinute);
    if (!Number.isFinite(startX) || !Number.isFinite(endX)) return null;

    const left = Math.max(chartArea.left, startX);
    const right = Math.min(chartArea.right, endX);
    const width = right - left;
    const iconRadius = 14;
    if (width < iconRadius * 2 + 4) return null;

    const cx = left + (width / 2);
    const cy = (chartArea.top + chartArea.bottom) / 2;

    ctx.beginPath();
    ctx.arc(cx, cy, iconRadius, 0, Math.PI * 2);
    ctx.fillStyle = isHovered ? '#2e8eaa' : '#011e41';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();

    if (this.cycleIconImage?.complete && this.cycleIconImage.naturalWidth > 0) {
      const size = 16;
      ctx.drawImage(this.cycleIconImage, cx - (size / 2), cy - (size / 2), size, size);
    }

    return { cx, cy, radius: iconRadius };
  }

  private drawPulseWaveIcon(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    width: number,
    height: number,
    color: string
  ): void {
    if (this.pulseIconImage?.complete && this.pulseIconImage.naturalWidth > 0) {
      ctx.drawImage(this.pulseIconImage, cx - (width / 2), cy - (height / 2), width, height);
      return;
    }

    const left = cx - (width / 2);
    const top = cy - (height / 2);
    const scaleX = width / 24;
    const scaleY = height / 16;
    const px = (x: number): number => left + (x * scaleX);
    const py = (y: number): number => top + (y * scaleY);

    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1.6, height * 0.22);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(px(2), py(13));
    ctx.lineTo(px(7), py(13));
    ctx.lineTo(px(7), py(5));
    ctx.lineTo(px(17), py(5));
    ctx.lineTo(px(17), py(13));
    ctx.lineTo(px(22), py(13));
    ctx.stroke();
    ctx.restore();
  }

  private drawDlPulseRegionIcon(
    ctx: CanvasRenderingContext2D,
    chartArea: { top: number; bottom: number; left: number; right: number },
    xScale: { getPixelForValue: (value: number) => number },
    region: DlPulseRegion,
    isHovered: boolean
  ): { cx: number; cy: number; radius: number } | null {
    const startMinute = Math.max(0, Math.min(DL_MINUTES_PER_DAY, region.startMinuteOfDay));
    const endMinute = Math.max(startMinute + 1, Math.min(DL_MINUTES_PER_DAY, region.endMinuteOfDay));
    const anchorMinute = startMinute + ((endMinute - startMinute) / 2);

    const cx = xScale.getPixelForValue(anchorMinute);
    if (!Number.isFinite(cx)) return null;

    const iconRadius = 11;
    const cy = chartArea.top + iconRadius + 8;

    if (cx < chartArea.left - iconRadius || cx > chartArea.right + iconRadius) return null;

    ctx.beginPath();
    ctx.arc(cx, cy, iconRadius, 0, Math.PI * 2);
    ctx.fillStyle = isHovered ? '#2e8eaa' : '#011e41';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();

    this.drawPulseWaveIcon(ctx, cx, cy, 14, 9, '#fff');

    return { cx, cy, radius: iconRadius };
  }

}
